import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PricingCard = ({ 
  plan, 
  isPopular = false, 
  onSelectPlan 
}) => {
  const {
    name,
    subtitle,
    description,
    pricePrefix,
    price,
    priceSuffix,
    persona,
    tagline,
    includes,
    features,
    mobileFeatures,
    notes,
    cta,
    supportCopy: planSupportCopy
  } = plan || {};

  const buttonVariant = cta?.type === 'waitlist'
    ? 'secondary'
    : isPopular
      ? 'default'
      : 'secondary';

  const buttonClassName = cta?.type === 'start-trial' && (isPopular || buttonVariant === 'default')
    ? 'btn-primary'
    : '';

  const supportCopy = planSupportCopy ?? (cta?.type === 'waitlist'
    ? 'Early access — we’ll follow up with next steps.'
    : '14-day free trial • No credit card required');

  const badgeLabel = plan?.badge || (isPopular ? (plan?.id === 'beta' ? 'Early Access' : 'Most Popular') : null);

  return (
    <div className={`relative bg-card rounded-2xl border transition-all duration-300 hover:shadow-brand-interactive ${
      isPopular 
        ? 'border-secondary shadow-brand-interactive'
        : 'border-border hover:border-secondary/50'
    }`}>
      {/* Popular Badge */}
      {badgeLabel && (
        <div className="absolute -top-3 lg:-top-4 left-1/2 transform -translate-x-1/2">
          <div className="bg-gradient-accent px-2 lg:px-4 py-1 lg:py-2 rounded-full">
            <span className="text-xs lg:text-sm font-semibold text-white whitespace-nowrap">{badgeLabel}</span>
          </div>
        </div>
      )}
      <div className="p-3 lg:p-8 h-full flex flex-col">
        {/* Header */}
        <div className="text-center mb-2 lg:mb-8">
          <h3 className="text-sm lg:text-2xl font-bold text-foreground mb-1">{name}</h3>
          {subtitle && (
            <p className="text-[10px] lg:text-sm text-muted-foreground mb-1">{subtitle}</p>
          )}
          {/* Description - desktop only */}
          <p className="hidden lg:block text-muted-foreground mb-3">{description}</p>
          {/* Persona */}
          {persona && (
            <p className="text-[10px] lg:text-sm text-accent font-medium mb-1 lg:mb-3">{persona}</p>
          )}
          {/* Tagline - desktop only */}
          {tagline && (
            <p className="hidden lg:block text-xs text-muted-foreground mb-6">{tagline}</p>
          )}

          {/* Pricing */}
          <div className="mb-2 lg:mb-4">
            <div className="flex items-baseline justify-center gap-1 lg:gap-2">
              {pricePrefix && (
                <span className="text-[10px] lg:text-xs uppercase tracking-wider text-muted-foreground font-semibold">{pricePrefix}</span>
              )}
              <span className="text-xl lg:text-4xl font-bold text-foreground">{price}</span>
              {priceSuffix && (
                <span className="text-muted-foreground text-xs lg:text-base">{priceSuffix}</span>
              )}
            </div>
          </div>

          {/* CTA Button */}
          <Button
            variant={buttonVariant}
            size="sm"
            fullWidth
            className={`${buttonClassName} text-[10px] lg:text-sm py-1.5 lg:py-2`}
            onClick={() => onSelectPlan(plan)}
          >
            {cta?.label || 'Learn more'}
          </Button>

          {/* Support copy - hidden on mobile */}
          {supportCopy && (
            <p className="hidden lg:block text-xs text-muted-foreground mt-2">
              {supportCopy}
            </p>
          )}
        </div>

        {/* Features - short labels on mobile, full on desktop */}
        <div className="space-y-2 lg:space-y-6 text-left mt-2 lg:mt-3">
          {/* Includes text - hidden on mobile */}
          {includes && (
            <p className="hidden lg:block text-sm font-semibold text-foreground">{includes}</p>
          )}

          {/* Mobile features - short labels */}
          <ul className="space-y-0.5 lg:hidden">
            {mobileFeatures?.map((feature, index) => (
              <li key={feature || index} className="flex items-start space-x-1.5">
                <Icon
                  name="Check"
                  size={10}
                  className="text-accent mt-0.5 flex-shrink-0"
                />
                <span className="text-[9px] text-muted-foreground leading-tight">{feature}</span>
              </li>
            ))}
          </ul>

          {/* Desktop features - full labels */}
          <ul className="hidden lg:block space-y-2">
            {features?.map((feature, index) => (
              <li key={feature || index} className="flex items-start space-x-3">
                <Icon
                  name="Check"
                  size={16}
                  className="text-accent mt-0.5 flex-shrink-0"
                />
                <span className="text-sm text-muted-foreground">{feature}</span>
              </li>
            ))}
          </ul>

          {/* Notes - hidden on mobile */}
          {notes?.length > 0 && (
            <div className="hidden lg:block pt-4 border-t border-border space-y-2">
              {notes?.map((note, index) => (
                <p key={index} className="text-xs text-muted-foreground leading-relaxed">
                  {note}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingCard;